// src/components/homeComponents/ContactUs.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FaWhatsapp,
  FaInstagram,
  FaEnvelope,
  FaArrowRight,
} from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

/*
  THE LAST FRAME — contact.
  Giant "Let's talk." assembles word by word on scroll, three contact
  channels with magnetic glow cards, and a form with animated focus
  lines. Submit opens a pre-filled email out of the box (mailto) —
  swap in EmailJS (you use it on Siara/OrgaLife) where marked.
  NOTE for Amiri: replace the CONTACT links/handles with the real ones.
*/
const CONTACT = {
  whatsapp: "https://wa.me/38300000000", // ← your number
  instagram: "https://instagram.com/asites.agency", // ← your handle
  email: "hello@asites.agency", // ← your email
};

const ContactUs = () => {
  const sectionRef = useRef(null);
  const titleRefs = useRef([]);
  const cardRefs = useRef([]);
  const formRef = useRef(null);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", brand: "", message: "" });

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    /* ── EmailJS swap-in point ─────────────────────────────
       emailjs.send("SERVICE_ID", "TEMPLATE_ID", form, "PUBLIC_KEY")
         .then(() => setSent(true));
       For now: opens the visitor's mail app pre-filled. ── */
    const subject = encodeURIComponent(
      `Project inquiry — ${form.brand || form.name || "New brand"}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nBrand: ${form.brand}\n\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // headline words rise in
      gsap.fromTo(
        titleRefs.current,
        { yPercent: 110, opacity: 0 },
        {
          yPercent: 0,
          opacity: 1,
          stagger: 0.12,
          duration: 1,
          ease: "power4.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 70%" },
        }
      );
      // channel cards cascade
      gsap.fromTo(
        cardRefs.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 55%" },
        }
      );
      // form slides in
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: { trigger: formRef.current, start: "top 85%" },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const CHANNELS = [
    {
      Icon: FaWhatsapp,
      label: "WhatsApp",
      sub: "Fastest reply",
      href: CONTACT.whatsapp,
      accent: "#25d366",
    },
    {
      Icon: FaInstagram,
      label: "Instagram",
      sub: "See daily work",
      href: CONTACT.instagram,
      accent: "#e6608a",
    },
    {
      Icon: FaEnvelope,
      label: "Email",
      sub: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      accent: "#9281dd",
    },
  ];

  return (
    <Section ref={sectionRef} id="contact">
      <div className="aura" />

      <div className="head">
        <span className="kicker">THE LAST FRAME</span>
        <h2>
          {["Let's", "talk."].map((w, i) => (
            <span className="mask" key={i}>
              <span
                className={`w ${i === 1 ? "hl" : ""}`}
                ref={(el) => (titleRefs.current[i] = el)}
              >
                {w}
              </span>
            </span>
          ))}
        </h2>
        <p className="sub">
          Tell us what you're building. We'll tell you exactly how we'd grow
          it.
        </p>
      </div>

      <div className="channels">
        {CHANNELS.map((c, i) => (
          <a
            className="card"
            key={c.label}
            href={c.href}
            target="_blank"
            rel="noreferrer"
            ref={(el) => (cardRefs.current[i] = el)}
            style={{ "--accent": c.accent }}
          >
            <c.Icon className="ico" />
            <div className="txt">
              <b>{c.label}</b>
              <span>{c.sub}</span>
            </div>
            <FaArrowRight className="arrow" />
          </a>
        ))}
      </div>

      <form className="form" ref={formRef} onSubmit={onSubmit}>
        <div className="row">
          <div className="field">
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              required
              placeholder=" "
              id="c-name"
            />
            <label htmlFor="c-name">Your name</label>
            <span className="bar" />
          </div>
          <div className="field">
            <input
              type="text"
              name="brand"
              value={form.brand}
              onChange={onChange}
              placeholder=" "
              id="c-brand"
            />
            <label htmlFor="c-brand">Brand / company</label>
            <span className="bar" />
          </div>
        </div>
        <div className="field">
          <textarea
            name="message"
            rows="4"
            value={form.message}
            onChange={onChange}
            required
            placeholder=" "
            id="c-msg"
          />
          <label htmlFor="c-msg">What are we building?</label>
          <span className="bar" />
        </div>

        <button type="submit" className="send">
          {sent ? "Opening your email…" : "Send it"} <FaArrowRight />
        </button>
      </form>

      <div className="copy">
        © {new Date().getFullYear()} ASites Agency — Web · Content · Marketing
      </div>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: radial-gradient(
    circle at 50% 0%,
    #221537 0%,
    #100818 45%,
    #08040c 75%
  );
  padding: 9em 7% 2.5em;
  overflow: hidden;

  .aura {
    position: absolute;
    top: -20%;
    left: 50%;
    transform: translateX(-50%);
    width: 70vw;
    height: 70vw;
    max-width: 850px;
    max-height: 850px;
    background: radial-gradient(circle, #5a4a8a44, transparent 60%);
    filter: blur(50px);
    pointer-events: none;
  }

  .head {
    position: relative;
    text-align: center;
    margin-bottom: 4em;
    .kicker {
      font-family: "Montserrat";
      letter-spacing: 0.45em;
      font-size: 0.72rem;
      color: #836caf;
      font-weight: 500;
    }
    h2 {
      margin-top: 0.2em;
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(3.4rem, 11vw, 9.5rem);
      line-height: 1;
      display: flex;
      justify-content: center;
      gap: 0.28em;
      flex-wrap: wrap;
      .mask {
        display: inline-block;
        overflow: hidden;
      }
      .w {
        display: inline-block;
        will-change: transform, opacity;
      }
      .hl {
        font-style: italic;
        color: #9281dd;
      }
    }
    .sub {
      color: #b3aec2;
      margin-top: 1em;
      font-size: clamp(0.95rem, 1.5vw, 1.15rem);
    }
  }

  .channels {
    position: relative;
    display: flex;
    justify-content: center;
    gap: 1em;
    flex-wrap: wrap;
    margin-bottom: 4.5em;

    .card {
      display: flex;
      align-items: center;
      gap: 0.9em;
      padding: 1.1em 1.4em;
      min-width: min(240px, 100%);
      border: 1px solid #241c38;
      border-radius: 16px;
      background: #0f0a1acc;
      text-decoration: none;
      color: #ffffff;
      transition: border-color 0.35s ease, transform 0.35s ease,
        box-shadow 0.35s ease;
      will-change: transform, opacity;

      .ico {
        font-size: 1.6rem;
        color: var(--accent);
        transition: transform 0.35s ease;
      }
      .txt {
        flex: 1;
        display: flex;
        flex-direction: column;
        b {
          font-family: "Montserrat";
          font-size: 0.95rem;
          font-weight: 600;
        }
        span {
          font-size: 0.78rem;
          color: #8a8595;
        }
      }
      .arrow {
        color: #463d6e;
        font-size: 0.85rem;
        transition: transform 0.35s ease, color 0.35s ease;
      }

      &:hover {
        border-color: var(--accent);
        transform: translateY(-4px);
        box-shadow: 0 20px 50px -20px var(--accent);
        .ico {
          transform: scale(1.15) rotate(-6deg);
        }
        .arrow {
          transform: translateX(5px);
          color: var(--accent);
        }
      }
    }
  }

  .form {
    position: relative;
    max-width: 640px;
    margin: 0 auto;
    will-change: transform, opacity;

    .row {
      display: flex;
      gap: 1.5em;
      @media (max-width: 640px) {
        flex-direction: column;
        gap: 0;
      }
    }

    .field {
      position: relative;
      flex: 1;
      margin-bottom: 2.2em;

      input,
      textarea {
        width: 100%;
        background: transparent;
        border: none;
        border-bottom: 1px solid #2a2140;
        padding: 0.9em 0 0.7em;
        color: #ffffff;
        font-family: "Montserrat";
        font-size: 1rem;
        outline: none;
        resize: vertical;
      }

      label {
        position: absolute;
        left: 0;
        top: 0.9em;
        color: #6f6a7c;
        font-size: 1rem;
        pointer-events: none;
        transition: 0.3s ease;
      }

      input:focus + label,
      input:not(:placeholder-shown) + label,
      textarea:focus + label,
      textarea:not(:placeholder-shown) + label {
        top: -0.8em;
        font-size: 0.72rem;
        color: #9281dd;
        letter-spacing: 0.08em;
      }

      .bar {
        position: absolute;
        left: 0;
        bottom: 0;
        height: 2px;
        width: 100%;
        background: linear-gradient(90deg, #9281dd, #b9c5fb);
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
      }
      input:focus ~ .bar,
      textarea:focus ~ .bar {
        transform: scaleX(1);
      }
    }

    .send {
      display: inline-flex;
      align-items: center;
      gap: 0.7em;
      font-family: "Montserrat";
      font-weight: 600;
      font-size: 1rem;
      padding: 16px 34px;
      border-radius: 30px;
      border: none;
      cursor: pointer;
      background: linear-gradient(120deg, #9281dd, #b9c5fb);
      color: #100a18;
      transition: 0.35s;
      svg {
        transition: transform 0.35s ease;
      }
      &:hover {
        box-shadow: 0 0 34px -6px #9281dd;
        transform: translateY(-2px);
        svg {
          transform: translateX(4px);
        }
      }
    }
  }

  .copy {
    position: relative;
    text-align: center;
    color: #4f4a5c;
    font-size: 0.8rem;
    padding-top: 5em;
  }
`;

export default ContactUs;
